import * as React from 'react'

import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import Magnify from 'mdi-material-ui/Magnify'
import FileDocumentOutline from 'mdi-material-ui/FileDocumentOutline'
import { Card, CardContent, Divider, FormHelperText } from '@mui/material'

import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Reports, Variation } from '@/@core/services/app.service'
import { useTranslation } from 'react-i18next'
import { unique } from '@/@core/utils/utils'

const schema = yup.object().shape({
  name: yup.string().required(),
  templatePath: yup.string().required(),
  result: yup.string().required()
})

const DragReport = React.forwardRef((props, ref) => {
  const { t } = useTranslation()
  const columns: GridColDef[] = [
    { field: 'gene', headerName: t('gene'), sortable: false },
    { field: 'base', headerName: t('Base change'), sortable: false },
    {
      field: 'homozygosity',
      headerName: t('Homozygosity'),
      sortable: false
    },
    {
      field: 'gAD',
      headerName: 'gAD',
      sortable: false
    },
    {
      field: 'fractionation',
      headerName: t('grading'),
      sortable: false
    },
    {
      field: 'codingEffect',
      headerName: 'codingEffect',
      sortable: false
    },
    {
      field: 'type',
      headerName: t('type'),
      sortable: false
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: () => [
        <GridActionsCellItem icon={<Magnify />} label="delete" key={'delete'} />
      ]
    }
  ]
  const defaultValues = {
    name: '',
    templatePath: '1',
    result: 'positive' as 'positive' | 'negative'
  }
  const [template, setTemplate] = React.useState('1')
  const [result, setResult] = React.useState('positive')
  const [name, setName] = React.useState('')
  const pagination = {
    pageNum: 0,
    pageSize: 10
  }
  const [page, setPage] = React.useState(pagination)
  const variantsReport = JSON.parse(
    localStorage.getItem('variantsReport') as string
  ) as {
    KeyFinding: Variation[] | null
    IncidentalDiscovery: Variation[] | null
    CarrierScreening: Variation[] | null
    ACMGDiscovery: Variation[] | null
  } | null
  let list: any[] = []

  Object.getOwnPropertyNames(variantsReport).map((key) => {
    //
    const arr = (
      variantsReport
        ? variantsReport[key as 'KeyFinding']
          ? variantsReport[key as 'KeyFinding']
          : []
        : []
    ) as Variation[] | []

    //

    list = unique([...list, ...arr], 'id')
  })

  // ** Hooks
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema)
  })

  const handleChange = (event: SelectChangeEvent) => {
    setTemplate(event.target.value)
  }

  const handleSave = (data: Reports) => {
    //
  }

  React.useImperativeHandle(ref, () => {
    return {}
  })

  return (
    <>
      <Typography fontSize={18}>{t('Create new report')}</Typography>

      <Divider />

      <Grid container sx={{ padding: '2% 1%' }}>
        <Grid item sx={{ width: '100%' }}>
          <Grid
            container
            sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}
          >
            <Grid item xs={2} sx={{ textAlign: 'end', paddingRight: 10 }}>
              <Typography>{t('name')}</Typography>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth sx={{ mb: 4 }}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={(e) => {
                        onChange(e)
                      }}
                      error={Boolean(errors.name)}
                      size="small"
                    />
                  )}
                />

                {errors.name && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {errors.name.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Grid
            container
            sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}
          >
            <Grid item xs={2} sx={{ textAlign: 'end', paddingRight: 10 }}>
              <Typography>{t('Select template')}</Typography>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth sx={{ mb: 4 }}>
                <Controller
                  name="templatePath"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={value}
                      onChange={onChange}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value={'1'}>
                        Defaut template: Genome Light Report Template
                      </MenuItem>

                      <MenuItem value={'2'}>
                        {t('Default template: Chinese report template')}
                      </MenuItem>

                      <MenuItem value={'3'}>Cred Diagnostics</MenuItem>
                    </Select>
                  )}
                />

                {errors.templatePath && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {errors.templatePath.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Grid
            container
            sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}
          >
            <Grid item xs={2} sx={{ textAlign: 'end', paddingRight: 10 }}>
              <Typography>{t('Test Result')}</Typography>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth sx={{ mb: 4 }}>
                <Controller
                  name="result"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      value={value}
                      onChange={onChange}
                    >
                      <FormControlLabel
                        value="positive"
                        control={<Radio />}
                        label={t('positive')}
                      />

                      <FormControlLabel
                        value="negative"
                        control={<Radio />}
                        label={t('negative')}
                      />
                    </RadioGroup>
                  )}
                />

                {errors.result && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {errors.result.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Grid
            container
            sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}
          >
            <Grid item xs={2} sx={{ textAlign: 'end', paddingRight: 10 }}>
              <Typography>SNV / INDEL</Typography>
            </Grid>

            <Grid item xs={10}>
              <DataGrid
                rows={list}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      page: page.pageNum,
                      pageSize: page.pageSize
                    }
                  }
                }}
                pageSizeOptions={[5, 10, 20, 100]}
                checkboxSelection
                disableColumnMenu
                onCellClick={(params: any) => {
                  //
                }}
                onPaginationModelChange={(model: any) => {
                  if (model.pageSize === 8) return

                  setPage({
                    pageNum: model.page,
                    pageSize: model.pageSize
                  })
                }}
                rowCount={10}
                autoHeight
              />
            </Grid>
          </Grid>

          {/* <Grid container sx={{ justifyContent: 'center' }}>
              <Grid item xs={6.3}>
                <Button
                  variant="contained"
                  startIcon={<FileDocumentOutline />}
                  size="small"
                  sx={{ marginLeft: 2, marginBottom: 3 }}
                  type="submit"
                >
                  {t('Create new report')}
                </Button>
              </Grid>
            </Grid> */}
        </Grid>
      </Grid>
    </>
  )
})

export default DragReport
